import { Link, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { AuthLayout } from 'modules/layouts/AuthLayout';
import Input from 'modules/shared-components/input/Input';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import RecaptchaField from 'modules/authentication/components/RecaptchaField';
import { ApiErrorNotification } from 'modules/shared-components/notification/ApiErrorNotification';
import { Button } from 'modules/shared-components/button/button';
import UserService, {
  userServiceKeys,
} from 'api/services/httpServices/UserService';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ReactComponent as GoogleLogo } from 'assets/img/icons/google-logo.svg';
import { useState } from 'react';
import { useUserState } from 'modules/user/UserContext';
import { TwoFactorModal } from 'modules/authentication/components/TwoFactorAuthentication';
import config from 'config';
import { LoginWithTelegram } from './TelegramLoginButton';
import { NotificationDex } from '../dex/components/alerts/notification';
import { manuallyRefreshToken } from 'modules/user/userCookies';
import { useWebsocketConnect } from 'api/services/websocket/useWebsocketConnect';
import { WebSocketContextType } from 'api/services/websocket/websocketProvider';
import { UserV1 } from 'api/types/httpsTypes/user';

export const handlePostLoginAuth = async (
  userData: any,
  websocket: Pick<
    WebSocketContextType,
    'connect' | 'waitForSocketOpen' | 'sendAuth'
  >,
  loginUser: (user: UserV1) => void,
  isConnected: boolean | undefined
): Promise<void> => {
  try {
    loginUser(userData); // store user in context

    const token = await manuallyRefreshToken(); // set cookie/access token
    if (!token) {
      console.warn('❌ No token returned after manual refresh');
      return;
    }

    if (!isConnected) {
      websocket.connect();
      await websocket.waitForSocketOpen();
    }

    websocket.sendAuth();
  } catch (err) {
    console.error('🔴 Failed during post-login WebSocket setup:', err);
  }
};

const schema = yup.object({
  email: yup
    .string()
    .typeError('Only strings are acceptable')
    .email('Incorrect email format')
    .required('Required'),
  password: yup.string().required('Required'),

  captcha: config.RECAPTCHA_SITE_KEY
    ? yup.string().required('Incorrect captcha')
    : yup.string(),
});

type FormValues = yup.InferType<typeof schema>;

type Props = {
  initialState?: Partial<FormValues>;
};

const Login = ({ initialState }: Props) => {
  const { state: routeState } = useLocation<{ message: string }>();
  const {
    formState: { errors },
    control,
    register,
    handleSubmit,
    getValues,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: initialState,
  });

  const { loginUser, startTwoFactor } = useUserState();

  const { data: csrfToken } = useQuery({
    queryFn: UserService.generateCSRFToken,
    queryKey: userServiceKeys.generateCSRFToken(),
  });
  const { sendAuth, connect, waitForSocketOpen, isConnected } =
    useWebsocketConnect();

  const { mutate, error, isLoading, data } = useMutation({
    mutationFn: UserService.login,
    onSuccess: async ({ user_data, otpMechanism }) => {
      if (user_data) {
        await handlePostLoginAuth(
          user_data,
          { connect, waitForSocketOpen, sendAuth },
          loginUser,
          isConnected
        );
      } else {
        startTwoFactor({ email: getValues('email'), mechanism: otpMechanism! });
      }
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutate({
      email_address: values.email,
      password: values.password,
      csrf_token: csrfToken || '',
      reCAPTCHA_token: values.captcha,
    });
  });

  return (
    <AuthLayout>
      <div className="lg:grid lg:grid-cols-2 ">
        <div className="px-4 lg:px-10">
          <h1 className="text-2xl lg:text-5xl xxs:text-center lg:text-left">
            Manage and automate your crypto investments with Mizar
          </h1>
          <div className="pt-16 hidden lg:block">
            <h5 className="text-2xl xxs:mb-1">Crypto Copy-Trading</h5>
            <p className="text-lg m-0">
              Copy successful traders and watch your portfolio grow.
            </p>
            <h5 className="text-2xl  xxs:mb-1 pt-8">Bots & Trading Tools</h5>
            <p className="text-lg m-0">
              Create trading bots and use smart trading tools.
            </p>
            <h5 className="text-2xl  xxs:mb-1 pt-8">No fixed fees</h5>
            <p className="text-lg m-0">
              No subscription fee, monthly fee or credit card required.
            </p>
          </div>
        </div>

        <div className="xxs:px-4 lg:px-24">
          <div className="py-10 px-6 bg-black-700 shadow-lg rounded-lg">
            <div>
              {routeState?.message && (
                <NotificationDex type="info" className="xxs:mb-4">
                  {routeState?.message}
                </NotificationDex>
              )}
              <p className="text-lg ">Login and automate your investment</p>

              <form
                onSubmit={(e) => {
                  void onSubmit(e);
                }}
              >
                <div className="xxs:flex flex-col xxs:mt-3 xxs:mb-4">
                  <Input
                    {...register('email')}
                    label="Email Address"
                    placeholder="Your email address"
                    errorText={errors.email?.message}
                    data-testid="SignUp-form-email-input"
                  />
                </div>

                <div className="xxs:flex flex-col xxs:mt-3 xxs:mb-4">
                  <Input
                    {...register('password')}
                    type="password"
                    label="Password"
                    placeholder="**********"
                    errorText={errors.password?.message}
                  />
                  <Link
                    to="/request-password-reset"
                    className="text-right text-xs xxs:mt-1"
                  >
                    Forgot your password?
                  </Link>
                </div>

                <RecaptchaField control={control} />

                <ApiErrorNotification error={error} className="xxs:my-2" />

                <Button
                  variant="primary"
                  type="submit"
                  className="w-full xxs:mb-2"
                  size="large"
                  loading={isLoading}
                >
                  Login
                </Button>
                <LoginWithGoogle />
                <div className="xxs:mt-2">
                  <LoginWithTelegram>Login with telegram</LoginWithTelegram>
                </div>

                <p className="font-sm pt-2 text-center">
                  Don&apos;t have an account?{' '}
                  <Link
                    to="sign-up"
                    className="text-blue-500 hover:text-blue-600"
                  >
                    Sign Up
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      {data?.otpMechanism && (
        <LoginTwoFactor
          email={getValues('email')}
          mechanism={data.otpMechanism}
        />
      )}
    </AuthLayout>
  );
};
export default Login;

function LoginWithGoogle() {
  const { user, loginUser, startTwoFactor } = useUserState();
  const [isInProgress, setIsInProgress] = useState(false);
  const { sendAuth, connect, waitForSocketOpen, isConnected } =
    useWebsocketConnect();

  const { mutate, error, isLoading } = useMutation({
    mutationFn: UserService.loginWithGoogle,
    onSuccess: async ({ user_data, email, otpMechanism }) => {
      if (user_data) {
        await handlePostLoginAuth(
          user_data,
          { connect, waitForSocketOpen, sendAuth },
          loginUser,
          isConnected
        );
      } else {
        startTwoFactor({ email, mechanism: otpMechanism! });
      }
    },
  });

  const { data: csrfToken } = useQuery({
    queryFn: UserService.generateCSRFToken,
    queryKey: userServiceKeys.generateCSRFToken(),
  });

  const login = useGoogleLogin({
    onSuccess: (codeResponse) => {
      setIsInProgress(false);
      mutate({
        ...codeResponse,
        csrf_token: csrfToken || '',
      });
    },
    onError: () => {
      setIsInProgress(false);
    },
    onNonOAuthError: () => {
      setIsInProgress(false);
    },
    flow: 'auth-code',
  });

  return (
    <>
      {error && <ApiErrorNotification className="xxs:my-2" error={error} />}
      <Button
        onClick={() => {
          setIsInProgress(true);
          login();
        }}
        loading={isInProgress || isLoading}
        size="large"
        type="button"
        className="w-full xxs:bg-white hover:bg-white-600 text-black-700 xxs:py-1.5"
      >
        <GoogleLogo title="google logo" height="32" />
        <span className="xxs:pl-2">LOGIN WITH GOOGLE</span>
      </Button>
      {user && 'mechanism' in user && (
        <LoginTwoFactor email={user.email} mechanism={user.mechanism} />
      )}
    </>
  );
}

function LoginTwoFactor({
  email,
  mechanism,
}: {
  mechanism: 'email' | 'app';
  email: string;
}) {
  const { logoutUser, loginUser } = useUserState();
  const { sendAuth, connect, waitForSocketOpen, isConnected } =
    useWebsocketConnect();

  const {
    mutate: validate2Fa,
    isLoading,
    error,
  } = useMutation({
    mutationFn: UserService.validate2FA,
    onSuccess: async ({ user_data }) => {
      await handlePostLoginAuth(
        user_data,
        { connect, waitForSocketOpen, sendAuth },
        loginUser,
        isConnected
      );
    },
  });

  return (
    <>
      <TwoFactorModal
        handleClose={() => {
          logoutUser();
        }}
        onSubmit={({ code }) => {
          validate2Fa({ email_address: email, otp: code });
        }}
        errors={<ApiErrorNotification error={error} />}
        isSubmitLoading={isLoading}
        mechanism={mechanism}
      />
    </>
  );
}
